/**
 * Axios Instance Configuration
 * Centralized API client with interceptors for authentication and error handling
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { jwtDecode } from 'jwt-decode'
import { tokenManager } from '@/utils/tokenManager'
import { API_CONFIG, API_HEADERS } from '@/config/api.constants'
import { getBackendUrl } from '@/config/environment'
import { UrlGenerator } from '@/utils/url-generator.util'
import { lockApp } from '@/store/appLock.store'
import { store } from '@/app/store'
import { resetContextData, resetAuthState } from '@/store/slices/auth.slice'
import { resetPermissions } from '@/store/slices/permissions.slice'
import { resetNavigation } from '@/store/slices/navigation.slice'
import { isRelaxedAutoLogoutEnabled } from '@/utils/auth-relax-auto-logout.util'
import { isPublicRoute } from '@/utils/route.util'
import { isPublicApiPath } from '@/config/public-api.constants'
import { mergeAbortSignal } from '@/lib/session/request-abort-hub'
import {
  isBrowserOffline,
  buildOfflineRejectedError,
} from '@/lib/session/offline-queue'
import { coordinate401SessionRecovery } from '@/lib/session/session-recovery-coordinator'
import { forceLogoutInvalidSession, isUserNotFoundApiError } from '@/lib/session/auth-session-invalid.util'
import { logger } from '@/lib/logger'

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.FULL_BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending httpOnly cookies
})

// Request interceptor - Add auth token and tenant context
// Following old code pattern:
// - /auth/select-context and /navigation/user-context use accessToken
// - All other requests use contextToken (when available)
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.baseURL = `${getBackendUrl()}/api`

    config.signal = mergeAbortSignal(
      config.signal as AbortSignal | undefined
    ) as AbortSignal

    if (isBrowserOffline()) {
      return Promise.reject(buildOfflineRejectedError(config.url))
    }

    const urlPath = config.url?.split('?')[0] ?? ''
    const isPublicApi = isPublicApiPath(urlPath)

    /** Workspace bootstrap & navigation PCS routes MUST send context JWT — never access-only anon. */
    const requiresExplicitWorkspaceContextJwt =
      urlPath.includes('/auth/workspace-bootstrap') ||
      (urlPath.includes('/navigation/') && !urlPath.includes('/navigation/user-context'))

    const isOnboardingSessionByUser =
      urlPath === '/onboarding/session' || urlPath === '/onboarding/session/complete'

    const needsAccessToken =
      !isPublicApi &&
      (isOnboardingSessionByUser ||
      config.url?.includes('/auth/select-context') ||
      config.url?.includes('/auth/select-student-context') ||
      config.url?.includes('/navigation/user-context') ||
      config.url?.includes('/auth/refresh-token') ||
      config.url?.includes('/auth/refresh') ||
      config.url?.includes('/organization/create') ||
      config.url?.includes('/organization/invitations') ||
      (config.url?.startsWith('/platform-templates') && !config.url?.includes('/apply')) ||
      (config.url?.startsWith('/platform/') &&
        !config.url?.includes('/platform/form-management') &&
        !config.url?.includes('/platform/document-templates') &&
        !config.url?.includes('/platform/monitoring') &&
        !config.url?.includes('/platform/setup-templates') &&
        !config.url?.includes('/platform/jobs') &&
        !config.url?.includes('/platform/filter-values')))

    const contextTokenForRequest = tokenManager.getTokenForRequest('contextToken')

    const hasExistingAuth = config.headers.Authorization

    if (isPublicApi) {
      delete config.headers.Authorization
    } else if (!hasExistingAuth) {
      if (needsAccessToken) {
        const accessToken = tokenManager.getToken('accessToken')
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`
        }
      } else if (contextTokenForRequest) {
        config.headers.Authorization = `Bearer ${contextTokenForRequest}`
      }
    }

    const skipJwtGate = Boolean((config as InternalAxiosRequestConfig & { skipWorkspaceContextJwt?: boolean }).skipWorkspaceContextJwt)

    const authHeader =
      typeof config.headers.Authorization === 'string' ? config.headers.Authorization : ''
    const resolvedContextJwtMatch = /^Bearer\s+(\S+)/i.exec(authHeader)
    const resolvedJwt = resolvedContextJwtMatch?.[1]

    if (requiresExplicitWorkspaceContextJwt && !skipJwtGate && (!resolvedJwt || resolvedJwt.length < 10)) {
      return Promise.reject(
        new Error(
          `[Vidhyantra API] Workspace context JWT is required for ${urlPath}. Complete select-context before calling PCS navigation or bootstrap endpoints.`
        )
      )
    }

    const shouldLogJwt =
      typeof import.meta !== 'undefined' &&
      import.meta.env.DEV &&
      requiresExplicitWorkspaceContextJwt &&
      resolvedJwt

    if (shouldLogJwt) {
      try {
        const decoded = jwtDecode(resolvedJwt) as { email?: string; profileType?: string }
        logger.debug('[API] workspace-context request', `${config.method?.toUpperCase() ?? 'GET'} ${urlPath}`, {
          ctxEmail: decoded.email ?? null,
          profileType: decoded.profileType ?? null,
        })
      } catch {
        /* ignore decode errors — still send request */
      }
    }

    let orgSlug = sessionStorage.getItem('currentOrgSlug')
    let branchSlug = sessionStorage.getItem('currentBranchSlug')

    if (!orgSlug) {
      orgSlug = localStorage.getItem('currentOrgSlug')
      if (orgSlug) {
        sessionStorage.setItem('currentOrgSlug', orgSlug)
        localStorage.removeItem('currentOrgSlug')
      }
    }

    if (!branchSlug) {
      branchSlug = localStorage.getItem('currentBranchSlug')
      if (branchSlug) {
        sessionStorage.setItem('currentBranchSlug', branchSlug)
        localStorage.removeItem('currentBranchSlug')
      }
    }

    if (orgSlug) {
      config.headers[API_HEADERS.ORGANIZATION_SLUG] = orgSlug
    }

    if (branchSlug) {
      config.headers[API_HEADERS.BRANCH_SLUG] = branchSlug
    }

    config.headers[API_HEADERS.REQUEST_ID] = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

function isNetworkOrTimeout(err: AxiosError): boolean {
  return (
    !err.response &&
    (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED' || err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT')
  )
}

function canRetryNetwork(originalRequest: InternalAxiosRequestConfig): boolean {
  const method = (originalRequest.method || 'get').toUpperCase()
  if (['GET', 'HEAD', 'OPTIONS'].includes(method)) return true
  if (originalRequest.allowNetworkRetry === true) return true
  return false
}

// Response interceptor - Handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
      _networkRetry?: boolean
      _sessionRecoveryApplied?: boolean
      allowNetworkRetry?: boolean
      skipServerDownLock?: boolean
    }

    if (!originalRequest) {
      return Promise.reject(error)
    }

    const skipLock = originalRequest.skipServerDownLock
    const isLoginPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/login')
    const CRITICAL_PATHS = ['/auth/', '/navigation/user-context', '/auth/workspace-bootstrap', '/users/me/ui-preferences']
    const isCriticalRequest = originalRequest.url && CRITICAL_PATHS.some((p) => originalRequest.url!.includes(p))
    const status = error.response?.status
    const errResponse = !!error.response
    const networkLike =
      !errResponse ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNREFUSED' ||
      error.code === 'ETIMEDOUT'
    const is5xxFromCriticalEndpoint = (status === 502 || status === 503 || status === 504) && isCriticalRequest
    const isNetworkFailureOnCritical = networkLike && isCriticalRequest
    const shouldLockApp =
      !skipLock &&
      !isLoginPage &&
      (isNetworkFailureOnCritical || is5xxFromCriticalEndpoint) &&
      typeof window !== 'undefined'

    if (shouldLockApp && !import.meta.env.DEV) {
      lockApp('server-down')
    }
    if (shouldLockApp) {
      return Promise.reject(error)
    }

    // One retry for flaky networks / timeouts (safe methods only unless allowNetworkRetry)
    if (
      !originalRequest._networkRetry &&
      isNetworkOrTimeout(error) &&
      canRetryNetwork(originalRequest)
    ) {
      originalRequest._networkRetry = true
      return api(originalRequest)
    }

    if (error.response?.status === 401) {
      const requestUrl = originalRequest.url ?? ''
      /** Avoid deadlock: recovery uses these endpoints internally */
      if (
        requestUrl.includes('/auth/refresh-token') ||
        requestUrl.includes('/auth/select-context') ||
        requestUrl.includes('/auth/select-student-context')
      ) {
        return Promise.reject(error)
      }

      const errorCode = (error.response?.data as { code?: string })?.code
      const errorMessage = (error.response?.data as { message?: string })?.message

      if (isUserNotFoundApiError(error)) {
        forceLogoutInvalidSession()
        return Promise.reject(error)
      }

      const isContextTokenInvalid =
        errorCode === 'CONTEXT_TOKEN_INVALID' ||
        errorCode === 'LEGACY_TOKEN_REJECTED' ||
        errorCode === 'INVALID_TOKEN_STRUCTURE' ||
        errorMessage?.includes('context_id')

      const isAccessTokenInvalid =
        errorCode === 'ACCESS_TOKEN_EXPIRED' ||
        errorCode === 'INVALID_TOKEN' ||
        errorMessage?.includes('Invalid authentication token') ||
        errorMessage?.includes('Token expired')

      if (
        !originalRequest._sessionRecoveryApplied &&
        (isContextTokenInvalid || isAccessTokenInvalid)
      ) {
        originalRequest._sessionRecoveryApplied = true

        const ok = await coordinate401SessionRecovery({
          isAccessTokenInvalid: Boolean(isAccessTokenInvalid),
          isContextTokenInvalid: Boolean(isContextTokenInvalid),
        })

        if (!ok) {
          const currentPath = window.location.pathname
          if (!currentPath.includes('/login') && !currentPath.includes('/logout')) {
            sessionStorage.setItem('returnAfterContextSelection', currentPath)
            window.location.href = '/login?stage=context'
          }
          return Promise.reject(error)
        }

        const newToken = tokenManager.getTokenForRequest('contextToken')
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        }

        return api(originalRequest)
      }

      const isAuthEndpoint =
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/select-context') ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/request-otp') ||
        originalRequest.url?.includes('/auth/verify-otp')

      const isPublicRoutePath = isPublicRoute(window.location.pathname)

      const isOnPublicOnboardingPage =
        window.location.pathname.includes('/onboarding/complete-profile') ||
        window.location.pathname.includes('/onboarding/accept-invitation')
      const isStorageUploadOnPublicOnboarding =
        originalRequest.url?.includes('/storage/upload') && isOnPublicOnboardingPage

      const isOnMyProfilePage = window.location.pathname.includes('/user/my-profile')
      const isProfileFetchRequest =
        originalRequest.url?.includes('/get-employee-by-id/') || originalRequest.url?.includes('/get-student-by-id/')
      const isMyProfileFetch401 = isOnMyProfilePage && isProfileFetchRequest

      const isManualDraftFormRequest =
        originalRequest.url?.includes('/manual-onboarding/') && originalRequest.url?.includes('/draft-form')
      const isManualDraftForm401 = isManualDraftFormRequest

      const isAttendanceRequest =
        originalRequest.url?.includes('/student-attendance/') ||
        originalRequest.url?.includes('/employee-attendance/')
      const isAttendance401 = isAttendanceRequest

      if (
        isAuthEndpoint ||
        isStorageUploadOnPublicOnboarding ||
        isPublicRoutePath ||
        isMyProfileFetch401 ||
        isManualDraftForm401 ||
        isAttendance401
      ) {
        return Promise.reject(error)
      }

      if (typeof window !== 'undefined' && sessionStorage.getItem('logoutInProgress')) {
        return Promise.reject(error)
      }

      if (isRelaxedAutoLogoutEnabled()) {
        return Promise.reject(error)
      }

      try {
        store.dispatch(resetAuthState())
        store.dispatch(resetContextData())
        store.dispatch(resetPermissions())
        store.dispatch(resetNavigation())
      } catch (_) {
        // Store may be unavailable in some environments
      }
      sessionStorage.setItem('logoutInProgress', 'true')
      tokenManager.clearAllAuthData()
      try {
        sessionStorage.clear()
        sessionStorage.setItem('logoutInProgress', 'true')
      } catch {
        //
      }

      window.location.href = UrlGenerator.getBaseLogoutUrl()

      return Promise.reject(error)
    }

    handleApiError(error)
    return Promise.reject(error)
  }
)

export { getApiErrorMessage } from '@/lib/session/api-error'

function handleApiError(_error: AxiosError) {}

export default api
